import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel, data, useAcao, type TomEtiqueta } from '../comum';

/**
 * O briefing do dia.
 *
 * 🔴 **Duas coisas esta tela nunca faz.**
 *
 * A primeira: apresentar o briefing de ontem como se fosse o de hoje. O
 * servidor devolve `doDia`, e quando ele é falso a data aparece em destaque. Um
 * retrato velho lido como novo é pior que nenhum retrato — quem confia nele
 * deixa de olhar o parque.
 *
 * A segunda: esconder o que não foi possível medir. "Nenhum problema de patch"
 * e "não medimos patch em 40 máquinas" são a mesma frase para quem lê rápido, e
 * só a primeira faz alguém dormir tranquilo. As incertezas ficam visíveis, não
 * atrás de um `details`.
 */

interface Problema {
  readonly id: string;
  readonly type: string;
  readonly title: string;
  readonly description: string;
  readonly severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  readonly affectedDevices: { readonly count: number; readonly percentage: number | null };
  readonly score: number;
  /**
   * Nasce do histórico de briefings, não da IA.
   *
   * 🔴 Era `UNKNOWN` para todo problema porque ninguém passava a série aos
   * detectores. Com ela, "19 máquinas com patch crítico" ganha a metade que
   * faltava: eram 40 na semana passada, ou eram 4?
   */
  readonly trend?: {
    readonly direction: 'UP' | 'DOWN' | 'STABLE' | 'UNKNOWN';
    readonly magnitude: number | null;
    readonly period: string | null;
  };
}

/** A mesma leitura da tela de Tendências, para o problema. */
const SETA: Readonly<Record<string, { simbolo: string; tom: TomEtiqueta; texto: string }>> = {
  UP: { simbolo: '▲', tom: 'erro', texto: 'piorando' },
  DOWN: { simbolo: '▼', tom: 'ok', texto: 'melhorando' },
  STABLE: { simbolo: '=', tom: 'neutro', texto: 'estável' },
};

interface Explicacao {
  readonly problemId: string;
  readonly explanation?: string;
  readonly recommendation?: string;
}

interface BriefingDoDia {
  readonly dia: string;
  readonly geradoEm: string;
  readonly resumo: string;
  readonly problemas: readonly Problema[];
  readonly explicacoes: readonly Explicacao[];
  readonly incertezas: readonly string[];
  readonly totais: Record<string, number>;
}

interface Resposta {
  readonly briefing: BriefingDoDia | null;
  readonly doDia: boolean;
  readonly hoje: string;
  readonly aviso?: string;
}

const SEVERIDADE: Readonly<Record<string, { rotulo: string; tom: TomEtiqueta }>> = {
  CRITICAL: { rotulo: 'Crítico', tom: 'erro' },
  HIGH: { rotulo: 'Alto', tom: 'erro' },
  MEDIUM: { rotulo: 'Médio', tom: 'alerta' },
  LOW: { rotulo: 'Baixo', tom: 'neutro' },
};

export function Briefing({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [dados, setDados] = useState<Resposta | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setDados(await api.get<Resposta>('/api/briefing'));
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
    }
  }, [aoPerderSessao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const gerar = (): void => {
    void acao.rodar(async () => {
      const r = await api.post<{ jaExistia: boolean }>('/api/briefing/gerar', {});
      // A geração é assíncrona: a fila é que roda a análise e as chamadas de IA.
      // Prometer o resultado agora faria a tela mentir por alguns segundos.
      return r.jaExistia
        ? 'A geração de hoje já está na fila.'
        : 'Briefing enfileirado. Ele aparece aqui quando o worker terminar.';
    });
  };

  const b = dados?.briefing ?? null;

  return (
    <Painel
      titulo="Briefing do dia"
      sub="O que o parque tem de mais urgente, calculado sobre os dados de hoje."
    >
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      {dados !== null && !dados.doDia && (
        <Aviso tipo="alerta">
          {b === null
            ? 'Nenhum briefing gerado ainda.'
            : `Este briefing é de ${b.dia}, não de hoje (${dados.hoje}).`}{' '}
          <button className="encolhe" disabled={acao.executando} onClick={gerar}>
            Gerar agora
          </button>
        </Aviso>
      )}

      {b !== null && (
        <>
          <p>
            <strong>{b.resumo}</strong>{' '}
            <span className="suave">· gerado {data(b.geradoEm)}</span>
          </p>

          {b.problemas.length === 0 ? (
            <p className="suave">Nada exigindo ação no momento.</p>
          ) : (
            b.problemas.map((p) => {
              const sev = SEVERIDADE[p.severity] ?? { rotulo: p.severity, tom: 'neutro' as const };
              const exp = b.explicacoes.find((e) => e.problemId === p.id);
              return (
                <div
                  key={p.id}
                  style={{
                    borderBottom: '1px solid var(--borda)',
                    paddingBottom: 12,
                    marginBottom: 12,
                  }}
                >
                  <div className="linha">
                    <div>
                      <strong>{p.title}</strong>{' '}
                      <span className="suave">
                        · {p.affectedDevices.count} máquina(s)
                        {p.affectedDevices.percentage !== null &&
                          ` (${Math.round(p.affectedDevices.percentage * 100)}%)`}
                      </span>
                    </div>
                    <span className="linha encolhe" style={{ gap: 6 }}>
                      {/*
                        `UNKNOWN` não vira etiqueta. Desenhar "estável" sobre um
                        histórico que não existe afirmaria estabilidade que
                        ninguém mediu.
                      */}
                      {p.trend !== undefined && SETA[p.trend.direction] !== undefined && (
                        <Etiqueta
                          tom={SETA[p.trend.direction]?.tom ?? 'neutro'}
                          {...(p.trend.period === null ? {} : { titulo: p.trend.period })}
                        >
                          {SETA[p.trend.direction]?.simbolo}{' '}
                          {SETA[p.trend.direction]?.texto}
                          {p.trend.magnitude !== null &&
                            ` ${Math.round(p.trend.magnitude * 100)}%`}
                        </Etiqueta>
                      )}
                      <Etiqueta tom={sev.tom}>{sev.rotulo}</Etiqueta>
                    </span>
                  </div>
                  <p className="suave">{p.description}</p>
                  {/*
                    A explicação da IA vem separada e rotulada. O número, a
                    severidade e a contagem acima são determinísticos: o modelo
                    recebeu esses fatos prontos e não pode alterá-los.
                  */}
                  {exp?.explanation !== undefined && (
                    <p>
                      <span className="suave">Análise: </span>
                      {exp.explanation}
                    </p>
                  )}
                  {exp?.recommendation !== undefined && (
                    <p>
                      <span className="suave">Recomendação: </span>
                      {exp.recommendation}
                    </p>
                  )}
                </div>
              );
            })
          )}

          {b.incertezas.length > 0 && (
            <Aviso tipo="alerta">
              <strong>O que não foi possível medir:</strong>
              <ul>
                {b.incertezas.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </Aviso>
          )}

          {dados?.doDia === true && (
            <button className="encolhe" disabled={acao.executando} onClick={gerar}>
              Recalcular
            </button>
          )}
        </>
      )}
    </Painel>
  );
}
